import React, { useState, useEffect, useCallback } from 'react';
import { Button, Form, Badge, Dropdown } from 'react-bootstrap';
import moment from 'moment';
import { getAPI, postAPI, putAPI, deleteAPI } from 'apis/backend/apiNode';
import Icon from 'components/icon/Icon';
import EmailSendPopup from '../CommunComptabilite/EmailSendPopup';
import { updateRelanceCounter } from 'apis/backend/apiPython';

const RelanceFactureManager = ({ factureId, companyId, onRelanceChange }) => {
    const [relances, setRelances] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newRelanceRemark, setNewRelanceRemark] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editRemarkValue, setEditRemarkValue] = useState('');

    // Email popup state
    const [openEmail, setOpenEmail] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState('');

    // Fetch relances
    const fetchRelances = useCallback(async () => {
        if (!factureId) return;
        try {
            setLoading(true);
            const response = await getAPI("relanceFacture", `/facture/${factureId}`);
            const data = response || [];
            setRelances(data);
            onRelanceChange?.(data);
        } catch (error) {
            console.error('Failed to load relances', error);
        } finally {
            setLoading(false);
        }
    }, [factureId, onRelanceChange]);

    useEffect(() => {
        fetchRelances();
    }, []);

    const showNotification = (title, message, type) => alert(`${title}: ${message}`);

    // --- Normal relance (textarea) ---
    const handleCreateRelance = async () => {
        if (!newRelanceRemark.trim()) {
            showNotification('Info', 'Veuillez saisir une remarque', 'info');
            return;
        }
        try {
            setLoading(true);
            const response = await postAPI("relanceFacture", {
                facture_id: Number(factureId),
                companyId: companyId?._id || companyId,
                remarque: newRelanceRemark.trim(),
                statut: 'not_paid',
                type: 'normal',
            });
            if (response) {
                await updateRelanceCounter(factureId, 'tel', 'inc');
                setNewRelanceRemark('');
                await fetchRelances();
            }
        } catch (error) {
            showNotification('Erreur', "Impossible d'ajouter la relance", 'danger');
        } finally {
            setLoading(false);
        }
    };

    // --- Email relance : called after popup sends email ---
    const handleEmailSent = async (emailData) => {
        try {
            const response = await postAPI("relanceFacture", {
                facture_id: Number(factureId),
                companyId: companyId?._id || companyId,
                statut: 'not_paid',
                type: 'email',
                emailTemplateType: selectedTemplate,
                emailTo: emailData.to,
                emailSubject: emailData.subject,
                emailBody: emailData.body,
                attachments: emailData?.validAttachments?.length,
                remarque: `Email envoyé à ${emailData.to} – Objet : ${emailData.subject}`,
            });
            if (response) {
                await updateRelanceCounter(factureId, 'mail', 'inc');

                setSelectedTemplate('');
                await fetchRelances();
            }
        } catch (err) {
            console.error(err);
            showNotification('Erreur', "Échec de l'enregistrement de la relance", 'danger');
        }
    };

    // --- Edit handlers ---
    const startEdit = (relance) => {
        console.log("****relance", relance)
        setEditingId(relance._id);
        setEditRemarkValue(relance.remarque || '');
    };

    const handleSaveEdit = async (relance) => {
        if (editRemarkValue === (relance.remarque || '')) {
            setEditingId(null);
            return;
        }
        try {
            setLoading(true);
            const response = await putAPI("relanceFacture", { remarque: editRemarkValue }, `/${relance._id}`);
            if (response) {
                const updatedRelances = relances.map(r =>
                    r._id === relance._id ? { ...r, remarque: editRemarkValue } : r
                );
                setRelances(updatedRelances);
                onRelanceChange?.(updatedRelances);
            }
            setEditingId(null);
        } catch (error) {
            showNotification('Erreur', 'Modification impossible', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditRemarkValue('');
    };

    // --- Delete handler ---
    const handleDeleteRelance = async (relance) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette relance ?")) return;
        try {
            setLoading(true);
            await deleteAPI("relanceFacture", `/${relance._id}`);
            const newRelances = relances.filter(r => r._id !== relance._id);
            await updateRelanceCounter(factureId, 'tel', 'dec');
            setRelances(newRelances);
            onRelanceChange?.(newRelances);
        } catch (error) {
            showNotification('Erreur', 'Suppression impossible', 'danger');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relance-container">
            {/* Header */}
            <div className="d-flex align-items-center mb-4">
                <Icon icon="FileText" className="fs-4 text-primary me-2" />
                <h5 className="mb-0 fw-bold">Gestion des relances</h5>
            </div>

            {/* Two action cards */}
            <div className="row g-4 mb-5">
                {/* Card 1: Normal relance */}
                <div className="col-md-6">
                    <div className="card h-100 shadow-sm">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-3">
                                <Icon icon="Edit" className="text-success me-2" />
                                <h6 className="mb-0">Relance par Appel</h6>
                            </div>
                            <textarea
                                className="form-control mb-3"
                                rows="3"
                                placeholder="Raison, commentaire..."
                                value={newRelanceRemark}
                                onChange={(e) => setNewRelanceRemark(e.target.value)}
                                disabled={loading}
                            />
                            <Button
                                variant="success"
                                onClick={handleCreateRelance}
                                disabled={loading || !newRelanceRemark.trim()}
                                className="w-100"
                            >
                                <Icon icon="Plus" /> Ajouter cette relance
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Card 2: Email relance */}
                <div className="col-md-6">
                    <div className="card h-100 shadow-sm">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-3">
                                <Icon icon="Mail" className="text-primary me-2" />
                                <h6 className="mb-0">Relance par email</h6>
                            </div>
                            <Form.Group className="mb-3">
                                <Form.Label>Modèle d'email</Form.Label>
                                <Dropdown>
                                    <Dropdown.Toggle variant="outline-primary" className="w-100">
                                        {selectedTemplate || 'Choisir un modèle'}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="w-100">
                                        <Dropdown.Item onClick={() => setSelectedTemplate('relance1')}>
                                            Relance 1
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={() => setSelectedTemplate('relance2')}>
                                            Relance 2
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={() => setSelectedTemplate('relance3')}>
                                            Relance 3
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Form.Group>
                            <Button
                                variant="primary"
                                onClick={() => setOpenEmail(true)}
                                disabled={!selectedTemplate || loading}
                                className="w-100"
                            >
                                <Icon icon="Send" /> Envoyer cet email
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Unified timeline */}
            <div className="card shadow-sm">
                <div className="card-header bg-white">
                    <h6 className="mb-0">Historique complet des relances</h6>
                </div>
                <div className="card-body">
                    {relances.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                            <Icon icon="Inbox" size="2x" className="mb-2" />
                            <p className="mb-0">Aucune relance enregistrée</p>
                        </div>
                    ) : (
                        <div className="d-flex flex-column gap-3">
                            {[...relances]
                                .sort((a, b) => new Date(b.dateRelance) - new Date(a.dateRelance))
                                .map((relance, index, sortedArray) => {
                                    const isEditing = editingId === relance._id;
                                    const relanceNumber = sortedArray.length - index;
                                    const isEmail = relance.type === 'email';
                                    return (
                                        <div
                                            key={relance._id}
                                            className="p-3 border rounded bg-white shadow-sm"
                                            style={{ borderLeft: '4px solid #0d6efd' }}
                                        >
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <span className="fw-bold me-2">#{relanceNumber}</span>
                                                    <Badge bg={relance.type === 'email' ? 'info' : 'success'} className="me-2">
                                                        {relance.type === 'email' ? <Icon icon="Mail" /> : <Icon icon="phone" />}
                                                        {' '}{relance.type === 'email' ? 'Email' : 'Appel'}
                                                    </Badge>
                                                    <span className="small text-muted">
                                                        {moment(relance.dateRelance).fromNow()} – {moment(relance.dateRelance).format('DD/MM/YYYY HH:mm')}
                                                    </span>
                                                </div>
                                                {!isEmail &&
                                                    <div className="btn-group">
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            onClick={() => startEdit(relance)}
                                                            disabled={loading}
                                                            title="Modifier"
                                                        >
                                                            <Icon icon="Edit" />
                                                        </Button>
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            className="text-danger"
                                                            onClick={() => handleDeleteRelance(relance)}
                                                            disabled={loading}
                                                            title="Supprimer"
                                                        >
                                                            <Icon icon="Delete" />
                                                        </Button>
                                                    </div>
                                                }
                                            </div>
                                            {/* Content based on type */}
                                            {isEmail ? (
                                                <div className="mt-2">
                                                    <div className="mb-1">
                                                        <strong>Type de mail :</strong> {relance.emailTemplateType}
                                                    </div>
                                                    <div className="mb-1">
                                                        <strong>À :</strong> {relance.emailTo}
                                                    </div>
                                                    <div className="mb-2">
                                                        <strong>Objet :</strong> {relance.emailSubject}
                                                    </div>
                                                    <div
                                                        className="small border rounded p-2 bg-light"
                                                        style={{ maxHeight: '250px', overflowY: 'auto' }}
                                                    >
                                                        <div dangerouslySetInnerHTML={{ __html: relance.emailBody }} />
                                                    </div>
                                                </div>
                                            ) : (
                                                <>{isEditing ? (
                                                    <div className="mt-2">
                                                        <textarea
                                                            className="form-control mb-2"
                                                            rows="2"
                                                            value={editRemarkValue}
                                                            onChange={(e) => setEditRemarkValue(e.target.value)}
                                                            disabled={loading}
                                                        />
                                                        <div className="d-flex gap-2">
                                                            <Button
                                                                size="sm"
                                                                variant="success"
                                                                onClick={() => handleSaveEdit(relance)}
                                                                disabled={loading}
                                                            >
                                                                Enregistrer
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="secondary"
                                                                onClick={handleCancelEdit}
                                                                disabled={loading}
                                                            >
                                                                Annuler
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <p className="mt-2 text-secondary small" style={{ whiteSpace: 'pre-wrap' }}>
                                                        {relance.remarque || 'Aucune remarque'}
                                                    </p>
                                                )}
                                                </>
                                            )}



                                            <div className="mt-2">
                                                <small className="text-muted">
                                                    <Icon icon="Person" /> {relance.createdBy?.name || relance.createdBy || 'Inconnu'}
                                                </small>
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                </div>
            </div>

            {/* Email modal */}
            <EmailSendPopup
                isOpen={openEmail}
                setIsOpen={setOpenEmail}
                model="facture"
                recordId={factureId}
                fnSuccess={handleEmailSent}
                EmailType={selectedTemplate}
            />
        </div>
    );
};

export default RelanceFactureManager;